/**
 * video-to-sound/app.js — the page of /video-to-sound.
 *
 * Flow:  pick a video → (long video: choose a range) → PREPARE (proxy + scene
 * cuts + frames, prepare.js) → free ANALYSIS (what should be heard per scene,
 * in the reader's language) → the person edits, picks an engine / takes /
 * music → price shown before anything is sent → UPLOAD the small proxy →
 * GENERATE (server, fal) with progress and cancel → takes land on the timeline,
 * are heard in sync with the picture (mixer.js), scene by scene choosable and
 * re-voiceable → EXPORT an MP4 with the sound (VideoAudioLane, picture not
 * re-encoded) or the sound alone.
 *
 * COMPARE (suggestion #1728, 27.10.2026): one scene voiced by every engine
 * at once (compare.js decides what to order and names the takes) — each
 * take arrives as a chip named by its engine, the scene plays in a loop and
 * the chips (or keys 1–9) swap the sound in place (mixer.js cross-fades);
 * the take liked becomes «voice the whole range with this engine».
 *
 * Coordinates: S.span = the prepared part of the ORIGINAL video (seconds).
 * Scenes and the selection are relative to span.start. A generation carries
 * its range in ORIGINAL seconds; its takes cover exactly that range.
 *
 * ES2018 (no optional chaining): this file is served as is.
 */
(function () {
    'use strict';
    // window.__perPixel / window.__setText (timbrica-core.js) write a position or a text only when what is
    // shown changes. A page built before they existed can still be handed this file — it then writes the
    // plain way. Why: docs/why/primitives/per-pixel-writes.md.
    function textWrite(e, t) { if (window.__setText) return window.__setText(e, t); e.textContent = t; }

    var cfg = window.__vsCfg;
    if (!cfg) return;
    var T = cfg.i18n || {};
    var SLUG = cfg.slug || 'video-to-sound';
    var P = window.VideoSoundProgress;
    var VC = window.VideoSoundCompare;
    // This page shows every generation of its own in «My video sounds», the
    // running one with its live progress — the site-wide «still working /
    // ready» note (PendingResultCue) would be a second copy of that row here.
    try { window.__pendingJobAttached = { tool: SLUG, id: '*' }; } catch (_) {}
    function $(id) { return document.getElementById(id); }
    function fmt(t, map) { t = String(t == null ? '' : t); for (var k in map) if (map.hasOwnProperty(k)) t = t.split(':' + k).join(map[k]); return t; }
    function ae(action, extra) { try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, action: action }, extra || {})); } catch (_) {} }
    function stage(n) { try { if (window.__stage) window.__stage(n); } catch (_) {} }
    function toast(m, t) { try { if (window.__toast) window.__toast(m, t || 'info'); } catch (_) {} }
    function csrf() { var m = document.querySelector('meta[name="csrf-token"]'); return m ? m.content : ''; }
    function clock(s) { s = Math.max(0, s || 0); var m = Math.floor(s / 60), r = Math.floor(s - m * 60); return m + ':' + (r < 10 ? '0' : '') + r; }
    function clockD(s) { s = Math.max(0, s || 0); var m = Math.floor(s / 60), r = s - m * 60; return m + ':' + (r < 10 ? '0' : '') + r.toFixed(1); }
    function esc(s) { return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
    function saveSet(k, v) { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, k, v); } catch (_) {} }
    function loadSet(k, d) { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch (_) { return d; } }

    async function api(url, opts) {
        opts = opts || {};
        var h = { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
        if (opts.body && !(opts.body instanceof FormData)) h['Content-Type'] = 'application/json';
        if (opts.method && opts.method !== 'GET') h['X-CSRF-TOKEN'] = csrf();
        var r = await fetch(url, { method: opts.method || 'GET', headers: h, credentials: 'same-origin',
            body: opts.body ? (opts.body instanceof FormData ? opts.body : JSON.stringify(opts.body)) : undefined });
        var j = null;
        try { j = await r.json(); } catch (_) { j = null; }
        return { status: r.status, json: j || {} };
    }
    function q(url, extra) { var u = url + (url.indexOf('?') >= 0 ? '&' : '?') + 'locale=' + encodeURIComponent(cfg.locale); if (extra) u += '&' + extra; return u; }

    // ── state ─────────────────────────────────────────────────────────────────
    var S = {
        boot: null, enabled: false, authed: false, balance: null, shape: null,
        engine: loadSet('engine', ''), variants: {}, opts: { no_voices: loadSet('no_voices', true) !== false, asmr: false, music: false, music_prompt: '' },
        file: null, url: null, meta: null, hasAudio: false, previewOk: false,
        span: null, prep: null, scenes: [], sel: null, analysis: null,
        uploadId: null, uploadSpanKey: null,
        gens: {}, pick: [], musicGid: null, busy: false, gid: null, pollTimer: null, startedAt: 0,
        // attached: the generation followed was NOT ordered for the video now on
        // the page (found in the history after a reload, or the video was
        // switched) — when it finishes it is announced, not laid on this video.
        attached: false, etaKey: null, histLive: {}, lastStatus: null,
        gains: { ai: 1, music: 0.35, orig: 0 }, listen: 'none', aiTrack: null, musicTrack: null,
        prepAbort: null, exporting: false,
        // cmp: the engine comparison running (or last finished) on one scene —
        // {i, start, end, runs:{engine:{gid, st, n, msg}}, t0, live}. loop: the
        // scene replayed while takes are compared — {i, a, b} in video seconds.
        // hand[i]: the person chose this scene's take themselves; an order over
        // several scenes then does not take it away.
        cmp: null, loop: null, hand: []
    };

    var el = {
        root: $('vsRoot'), upload: $('vsUpload'), work: $('vsWork'), video: $('vsVideo'), screen: $('vsScreen'),
        noPrev: $('vsNoPrev'), bigPlay: $('vsBigPlay'), busy: $('vsBusy'), busyT: $('vsBusyT'), busyS: $('vsBusyS'), busyBar: $('vsBusyBar'),
        play: $('vsPlay'), time: $('vsTime'), listen: $('vsListen'), tl: $('vsTimeline'), selInfo: $('vsSelInfo'), selAll: $('vsSelAll'), split: $('vsSplit'),
        rangePick: $('vsRangePick'), rangeT: $('vsRangeT'), rangeS: $('vsRangeS'), rangeGo: $('vsRangeGo'),
        scenesP: $('vsScenesPanel'), an: $('vsAnalysis'), summary: $('vsSummary'), scenes: $('vsScenes'), reanalyze: $('vsReanalyze'),
        prog: $('vsProgress'), progBar: $('vsProgBar'), progText: $('vsProgText'), progTime: $('vsProgTime'), cancel: $('vsCancel'),
        err: $('vsError'), errMsg: $('vsErrorMsg'),
        result: $('vsResult'), refused: $('vsRefused'), takes: $('vsTakes'), gAi: $('vsGAi'), gAiV: $('vsGAiV'), gMusic: $('vsGMusic'), gMusicV: $('vsGMusicV'), gMusicRow: $('vsGMusicRow'),
        gOrig: $('vsGOrig'), gOrigV: $('vsGOrigV'), gOrigRow: $('vsGOrigRow'), fadeIn: $('vsFadeIn'), fadeOut: $('vsFadeOut'),
        exp: $('vsExport'), expWav: $('vsExportWav'), expMsg: $('vsExportMsg'), files: $('vsFiles'), retention: $('vsRetention'),
        engines: $('vsEngines'), noVoices: $('vsNoVoices'), asmrRow: $('vsAsmrRow'), asmr: $('vsAsmr'), music: $('vsMusic'), musicRow: $('vsMusicRow'), musicPrompt: $('vsMusicPrompt'),
        variants: $('vsVariants'), price: $('vsPrice'), rub: $('vsRub'), priceNote: $('vsPriceNote'), balance: $('vsBalance'), topup: $('vsTopup'),
        gate: $('vsGate'), go: $('vsGo'), hist: $('vsHistory'), histList: $('vsHist'),
        cmpRail: $('vsCmpRail'), cmpRailNote: $('vsCmpRailNote')
    };

    var tl = window.VideoSoundTimeline.create(el.tl, {
        labels: { start: T.tl_start, end: T.tl_end, scene: T.tl_scene },
        onSeek: function (t) { if (!S.span) return; S.loop = null; seekTo(S.span.start + t); },
        onSelect: function (s) { S.sel = { start: s.start, end: s.end }; renderSelInfo(); renderPrice(); renderPeopleHint(); if (!s.live) { renderScenes(); ae('select', { len: Math.round(s.end - s.start) }); } },
        onScene: function (i) { if (S.span && S.scenes[i]) { S.loop = null; seekTo(S.span.start + S.scenes[i].start + 0.01); } }
    });
    var player = window.VideoSoundMixer.player(el.video, { hasOrig: false });

    // ── bootstrap ─────────────────────────────────────────────────────────────
    api(q(cfg.api.bootstrap)).then(function (r) {
        var d = r.json || {};
        if (r.status !== 200) { gateText(T.disabled_msg); return; }
        S.boot = d; S.enabled = !!d.enabled; S.authed = !!d.authed; S.balance = d.balance;
        S.shape = d.price;
        if (!S.engine || !S.shape.engines[S.engine]) S.engine = d.default_engine || 'sonilo';
        Object.keys(S.shape.engines).forEach(function (k) { S.variants[k] = S.shape.engines[k].default_variants || 1; });
        renderEngines(); renderVariants(); renderOptions(); renderBalance(); renderPrice(); exampleMoney(); exampleEngines();
        if (S.authed) loadHistory();
        if (window.__refreshCsrf) { try { window.__refreshCsrf(); } catch (_) {} }
        var gen = new URLSearchParams(location.search).get('gen');
        if (gen && S.authed) openGeneration(parseInt(gen, 10), true);
    }).catch(function () { gateText(T.err_generic); });
    if (window.__tokenPrice && window.__tokenPrice.ready) window.__tokenPrice.ready().then(function () { renderPrice(); exampleMoney(); }).catch(function () {});
    else document.addEventListener('DOMContentLoaded', function () { if (window.__tokenPrice) window.__tokenPrice.ready().then(function () { renderPrice(); exampleMoney(); }).catch(function () {}); });

    // ── file ──────────────────────────────────────────────────────────────────
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(el.upload, onFile);
    else el.upload.addEventListener('tool-uploader:file', function (e) { var f = e.detail && (e.detail.file || (e.detail.files && e.detail.files[0])); if (f) onFile(f); });
    el.upload.addEventListener('tool-uploader:clear', function () { resetAll(); el.work.hidden = true; });

    function resetAll() {
        if (S.prepAbort) { try { S.prepAbort.abort(); } catch (_) {} }
        // A generation still on the server keeps being followed: stopping the
        // poll here used to leave «Adding sound…» on a dead button until reload.
        // It no longer belongs to the video on the page, so it is announced
        // when ready rather than laid on whatever video comes next.
        if (S.gid) S.attached = true;
        try { el.video.pause(); } catch (_) {}
        if (S.url) { try { URL.revokeObjectURL(S.url); } catch (_) {} }
        if (S.prep && S.prep.thumbs) S.prep.thumbs.forEach(function (t) { try { URL.revokeObjectURL(t.url); } catch (_) {} });
        S.file = null; S.url = null; S.meta = null; S.prep = null; S.span = null; S.scenes = []; S.sel = null; S.analysis = null;
        S.uploadId = null; S.uploadSpanKey = null; S.pick = []; S.hand = []; S.aiTrack = null; S.musicTrack = null; S.musicGid = null;
        // A comparison still on the server is left to it (each order lands in the
        // history); this video is gone, so the page stops following it.
        if (S.cmp && S.cmp.live) { if (S.cmp.timer) clearTimeout(S.cmp.timer); S.busy = false; }
        S.cmp = null; S.loop = null;
        el.scenesP.hidden = true; el.result.hidden = true; el.prog.hidden = !S.gid; el.err.hidden = true; el.rangePick.hidden = true;
        setListen('none', true); listenAvail();
        player.setBuffers({}, 0, 0);
        renderPrice();
    }

    async function onFile(file) {
        resetAll();
        S.file = file;
        el.work.hidden = false;
        stage('file_loaded');
        ae('file', { mb: Math.round(file.size / 1048576), ext: (file.name.split('.').pop() || '').toLowerCase().slice(0, 5) });
        S.url = URL.createObjectURL(file);
        el.video.src = S.url;
        var meta = await readMeta(el.video);
        S.previewOk = !!(meta && meta.width > 0);
        el.noPrev.hidden = S.previewOk;
        el.bigPlay.hidden = !S.previewOk;
        S.meta = meta || { duration: 0, width: 0, height: 0 };
        el.screen.classList.toggle('is-portrait', S.meta.height > S.meta.width * 1.05);
        try {
            if (window.MediaAudioProbe) { var pr = await window.MediaAudioProbe.probe(file); S.hasAudio = !!(pr && pr.hasAudio); if (!S.meta.duration && pr && pr.durationSec) S.meta.duration = pr.durationSec; }
        } catch (_) {}
        listenAvail();
        renderTime();
        var max = (S.boot && S.boot.limits && S.boot.limits.max_total_s) || cfg.maxMin * 60;
        if (!(S.meta.duration > 0)) {
            // No metadata (a format the browser will not open): prepare the whole file,
            // the FFmpeg lane reads the length itself.
            S.meta.duration = 0;
            prepare(0, 0);
            return;
        }
        if (S.meta.duration > max + 0.5) {
            // Long video: choose the part first, the timeline shows the whole file.
            tl.set({ duration: S.meta.duration, thumbs: [], scenes: [], sel: { start: 0, end: max }, maxSel: max });
            S.sel = { start: 0, end: max };
            el.rangePick.hidden = false;
            el.rangeT.textContent = fmt(T.range_pick_title, { min: cfg.maxMin });
            el.rangeS.textContent = ' ' + fmt(T.range_pick_text, { min: cfg.maxMin });
            S.pickingRange = true;
            quickThumbs(S.meta.duration).then(function (th) { if (S.pickingRange) tl.set({ thumbs: th }); });
            renderSelInfo();
            ae('range_pick', { dur: Math.round(S.meta.duration) });
            return;
        }
        prepare(0, S.meta.duration);
    }

    el.rangeGo.addEventListener('click', function () {
        if (!S.sel) return;
        S.pickingRange = false;
        el.rangePick.hidden = true;
        prepare(S.sel.start, S.sel.end);
    });

    function readMeta(v) {
        return new Promise(function (res) {
            var later = window.__visibleTimeout || function (fn, ms) { var id = setTimeout(fn, ms); return function () { clearTimeout(id); }; };
            var done = false;
            var fin = function (m) { if (done) return; done = true; if (stop) stop(); res(m); };
            var stop = later(function () { fin(null); }, 12000);
            v.onloadedmetadata = function () {
                // A <video> that "loads" a codec it cannot show reports 0×0 and no error
                // (MPEG-4 Part 2, ProRes…) — measured, see CLAUDE.md. Treat it as no preview.
                fin({ duration: isFinite(v.duration) ? v.duration : 0, width: v.videoWidth || 0, height: v.videoHeight || 0 });
            };
            v.onerror = function () { fin(null); };
        });
    }

    /* A filmstrip for choosing a range on a long video — a handful of seeks. */
    async function quickThumbs(dur) {
        if (!S.previewOk) return [];
        var v = document.createElement('video'); v.muted = true; v.preload = 'auto'; v.src = S.url;
        await new Promise(function (r) { v.onloadeddata = r; v.onerror = r; setTimeout(r, 6000); });
        var out = [], n = 16, cv = document.createElement('canvas');
        cv.height = 72; cv.width = Math.max(2, Math.round(72 * (v.videoWidth || 16) / (v.videoHeight || 9)));
        for (var i = 0; i < n; i++) {
            if (!S.pickingRange) break;
            await new Promise(function (r) { v.onseeked = r; setTimeout(r, 1500); v.currentTime = (i + 0.5) * dur / n; });
            try { cv.getContext('2d').drawImage(v, 0, 0, cv.width, cv.height); out.push({ t: (i + 0.5) * dur / n, url: cv.toDataURL('image/jpeg', 0.6) }); } catch (_) {}
        }
        v.removeAttribute('src'); try { v.load(); } catch (_) {}
        return out;
    }

    // ── prepare ───────────────────────────────────────────────────────────────
    async function prepare(start, end) {
        S.prepAbort = new AbortController();
        busy(true, T.prep_title, T.prep_sub, 0);
        stage('preparing');
        var t0 = performance.now();
        try {
            var res = await window.VideoSoundPrepare.run({
                file: S.file, start: start, end: end || S.meta.duration || 0, slug: SLUG,
                meta: { width: S.meta.width, height: S.meta.height, duration: S.meta.duration },
                // The server's upload cap — the proxy is sized to pass it.
                maxBytes: (+cfg.uploadMaxMb || 0) * 1048576,
                signal: S.prepAbort.signal,
                preferLane: S.previewOk ? null : 'ffmpeg',
                onProgress: function (p, lane) {
                    busy(true, T.prep_title, lane === 'ffmpeg' || lane === 'analyse' ? T.prep_ffmpeg : T.prep_sub, p);
                }
            });
            S.prep = res;
            if (!S.meta.duration) S.meta.duration = res.duration;
            S.span = { start: start, end: start + res.duration };
            var cuts = [0].concat(res.cuts || []);
            S.scenes = cuts.map(function (c, i) {
                return { start: c, end: i + 1 < cuts.length ? cuts[i + 1] : res.duration, text: '', en: '', people: false, edited: false };
            });
            capScenes(24);
            S.sel = { start: 0, end: res.duration };
            S.pick = S.scenes.map(function () { return null; });
            S.hand = S.scenes.map(function () { return false; });
            busy(false);
            renderTimeline(); renderScenes(); renderSelInfo(); renderPrice();
            el.scenesP.hidden = false;
            ae('prepared', { lane: res.lane, ms: Math.round(performance.now() - t0), scenes: S.scenes.length, dur: Math.round(res.duration) });
            stage('prepared');
            analyze();
            // A sound opened from «Мои генерации» before its video was chosen.
            if (S.pendingGen) { var pg = S.pendingGen; S.pendingGen = null; absorb(pg, true).then(function () { rebuildTrack(); renderScenes(); renderTimeline(); renderResult(); setListen('ai'); toast(T.history_applied, 'success'); }).catch(function () {}); }
        } catch (e) {
            busy(false);
            if (e && e.name === 'AbortError') return;
            showError(T.prep_failed);
            ae('prepare_error', { why: String((e && (e.detail || e.message)) || e).slice(0, 160) });
        }
    }

    /* The analysis takes up to 24 frames — at most that many scenes are
       described; shorter neighbours are merged first. */
    function capScenes(max) {
        while (S.scenes.length > max) {
            var bi = 1, bl = Infinity;
            for (var i = 1; i < S.scenes.length; i++) { var l = S.scenes[i].end - S.scenes[i - 1].start; if (l < bl) { bl = l; bi = i; } }
            S.scenes[bi - 1].end = S.scenes[bi].end;
            S.scenes.splice(bi, 1);
        }
    }

    function busy(on, title, sub, p) {
        el.busy.hidden = !on;
        if (!on) return;
        textWrite(el.busyT, title || '');
        textWrite(el.busyS, sub || '');
        el.busyBar.style.width = Math.round((p || 0) * 100) + '%';
    }

    // ── analysis ──────────────────────────────────────────────────────────────
    function framesForAnalysis() {
        var fr = (S.prep && S.prep.frames) || [], out = [];
        if (!fr.length) return out;
        var per = S.scenes.length <= 8 ? 2 : 1;
        S.scenes.forEach(function (s, i) {
            var want = per === 2 && s.end - s.start >= 3 ? [0.3, 0.72] : [0.5];
            want.forEach(function (f) {
                var t = s.start + (s.end - s.start) * f, best = null;
                fr.forEach(function (x) { if (!best || Math.abs(x.t - t) < Math.abs(best.t - t)) best = x; });
                if (best && !out.some(function (o) { return o.blob === best.blob && o.scene === i; })) out.push({ scene: i, t: best.t, blob: best.blob });
            });
        });
        return out.slice(0, (S.boot && S.boot.limits && S.boot.limits.max_frames) || 24);
    }
    function b64(blob) { return new Promise(function (r) { var fr = new FileReader(); fr.onload = function () { r(fr.result); }; fr.onerror = function () { r(null); }; fr.readAsDataURL(blob); }); }

    async function analyze() {
        if (!S.prep || !S.boot || !S.boot.analysis) { setAn('', false); return; }
        var frames = framesForAnalysis();
        if (!frames.length) { setAn(T.an_failed, false); return; }
        setAn(T.an_running, true);
        el.reanalyze.disabled = true;
        var body = { locale: cfg.locale, total: S.prep.duration, scenes: S.scenes.map(function (s) { return { start: s.start, end: s.end }; }), frames: [] };
        // Согласие на разбор за токены (public/js/lane-pay.js); Премиум тратит свой разбор дня первым.
        if (window.__lanePay) {
            if (window.__lanePay.agreed('video-to-sound')) body.pay = 'tokens';
            window.__lanePay.hide($('vsAnPay'));
        }
        for (var i = 0; i < frames.length; i++) {
            var d = await b64(frames[i].blob);
            if (d) body.frames.push({ scene: frames[i].scene, t: Math.round(frames[i].t * 100) / 100, img: d });
        }
        var t0 = performance.now();
        var r = await api(q(cfg.api.analyze), { method: 'POST', body: body }).catch(function () { return { status: 0, json: {} }; });
        el.reanalyze.disabled = false;
        if (r.status === 200 && r.json.ok) {
            var a = r.json.analysis || {};
            S.analysis = a;
            (a.scenes || []).forEach(function (x) {
                var s = S.scenes[x.i];
                if (!s || s.edited) return;
                s.text = x.local || x.en || ''; s.en = x.en || ''; s.people = !!x.people;
            });
            if (a.summary) { el.summary.textContent = a.summary; el.summary.hidden = false; }
            if (a.music_local && !el.musicPrompt.value) { el.musicPrompt.value = a.music_local; S.opts.music_prompt = a.music_local; S.opts.music_prompt_en = a.music_en || ''; }
            renderScenes();
            setAn(T.an_done + (typeof r.json.left === 'number' && r.json.left <= 3 ? ' · ' + fmt(T.an_left_fmt, { n: r.json.left }) : ''), false);
            if (r.json.paid_tokens > 0 && window.__lanePay) window.__lanePay.paid($('vsAnPay'), r.json);
            ae('analyzed', { ms: Math.round(performance.now() - t0), scenes: S.scenes.length, frames: body.frames.length, people: S.scenes.filter(function (s) { return s.people; }).length });
            stage('analyzed');
            return;
        }
        // The vendor account is out of funds: an order would fail the same way, so the
        // person is told now, not after describing every scene by hand.
        if (r.status === 503 && r.json.error === 'service_down') {
            setAn(r.json.msg || T.an_failed, false);
            if (r.json.msg) showError(r.json.msg);
            ae('analyze_failed', { status: 503, why: 'service_down' });
            return;
        }
        if (r.status === 429 && r.json.error === 'analyze_cap') { setAn(T.an_cap, false); ae('analyze_cap', {}); return; }
        // Разбор за токены: предложение с ценой вместо ошибки. Сцены можно описать и самим.
        if (window.__lanePay && window.__lanePay.show($('vsAnPay'), r.json, { slug: 'video-to-sound', run: analyze })) {
            setAn('', false);
            ae('analyze_offer', { status: r.status });
            return;
        }
        setAn(T.an_failed, false);
        ae('analyze_failed', { status: r.status });
    }
    function setAn(t, running) { el.an.textContent = t || ''; el.an.classList.toggle('is-run', !!running); }
    el.reanalyze.addEventListener('click', function () { S.scenes.forEach(function (s) { s.edited = false; }); analyze(); ae('reanalyze', {}); });

    // ── timeline, scenes ──────────────────────────────────────────────────────
    function renderTimeline() {
        if (!S.span) return;
        tl.set({
            duration: S.span.end - S.span.start,
            thumbs: (S.prep && S.prep.thumbs) || [],
            scenes: S.scenes.map(function (s, i) { return { start: s.start, end: s.end, state: S.pick[i] ? 'sound' : (s.pending ? 'pending' : 'none') }; }),
            sel: S.sel, maxSel: (S.boot && S.boot.limits && S.boot.limits.max_total_s) || 180
        });
    }
    function sceneThumb(s) {
        var th = (S.prep && S.prep.thumbs) || [], mid = (s.start + s.end) / 2, best = null;
        th.forEach(function (x) { if (!best || Math.abs(x.t - mid) < Math.abs(best.t - mid)) best = x; });
        return best ? best.url : '';
    }
    function renderScenes() {
        el.scenes.innerHTML = '';
        S.scenes.forEach(function (s, i) {
            var li = document.createElement('li');
            li.className = 'vs-sc';
            var inSel = S.sel && s.end > S.sel.start + 0.01 && s.start < S.sel.end - 0.01;
            li.classList.toggle('is-off', !inSel);
            var thumb = document.createElement('button');
            thumb.type = 'button'; thumb.className = 'vs-sc-thumb';
            thumb.style.backgroundImage = sceneThumb(s) ? 'url("' + sceneThumb(s) + '")' : '';
            thumb.setAttribute('aria-label', T.scene_play + ' ' + (i + 1));
            thumb.addEventListener('click', function () { playScene(i); });
            var main = document.createElement('div'); main.className = 'vs-sc-main';
            var head = document.createElement('div'); head.className = 'vs-sc-head';
            head.innerHTML = '<span class="vs-sc-t">' + esc(fmt(T.scene_label, { n: i + 1, from: clockD(s.start), to: clockD(s.end) })) + '</span>';
            if (i > 0) {
                var mg = document.createElement('button'); mg.type = 'button'; mg.className = 'vs-link'; mg.textContent = T.scene_merge;
                mg.addEventListener('click', function () { mergeScene(i); });
                head.appendChild(mg);
            }
            var ta = document.createElement('textarea');
            ta.className = 'vs-sc-text'; ta.rows = 2; ta.maxLength = 300; ta.value = s.text || ''; ta.placeholder = T.scene_ph;
            ta.setAttribute('aria-label', fmt(T.scene_label, { n: i + 1, from: clockD(s.start), to: clockD(s.end) }));
            ta.addEventListener('input', function () { s.text = ta.value; s.en = ''; s.edited = true; });
            main.appendChild(head); main.appendChild(ta);
            if (s.people) { var p = document.createElement('div'); p.className = 'vs-sc-people'; p.textContent = '🗣 ' + T.scene_people; main.appendChild(p); }
            var row = document.createElement('div'); row.className = 'vs-sc-row';
            var takes = takesFor(i);
            if (takes.length) {
                // Named by engine — «Подробный», «Эффекты», «Студия A/B» — in one
                // order on every scene; a radio group, the keys 1–9 pick too.
                var names = takeLabels();
                var tk = document.createElement('span'); tk.className = 'vs-sc-takes';
                tk.setAttribute('role', 'radiogroup'); tk.setAttribute('aria-label', fmt(T.scene_label, { n: i + 1, from: clockD(s.start), to: clockD(s.end) }));
                var lab = document.createElement('span'); lab.textContent = T.scene_sounds; tk.appendChild(lab);
                takes.forEach(function (t, k) {
                    var b = document.createElement('button'); b.type = 'button'; b.className = 'vs-chip';
                    var cur = S.pick[i], on = !!(cur && cur.gid === t.gid && cur.n === t.n);
                    var nm = names[t.gid + ':' + t.n];
                    b.classList.toggle('on', on);
                    b.setAttribute('role', 'radio'); b.setAttribute('aria-checked', on ? 'true' : 'false');
                    b.textContent = nm ? nm.label : fmt(T.take_fmt, { n: k + 1 });
                    if (takes.length > 1 && k < 9) b.title = fmt(T.take_key_fmt, { n: k + 1 });
                    b.addEventListener('click', function () { pickTake(i, t, k, 'chip'); });
                    tk.appendChild(b);
                });
                row.appendChild(tk);
            } else if (S.gensCount()) {
                var ns = document.createElement('span'); ns.className = 'vs-sc-takes'; ns.textContent = T.scene_nosound; row.appendChild(ns);
            }
            // The take heard here is silent where its engine refused: say so, and
            // offer the stretch to an engine that can take it — the plain «new
            // sound» button would send it straight back to the one that refused.
            // Not only the take heard now: an engine that refused this scene once
            // refuses it again (its checker answers about the frames), so its
            // «new sound» goes to another engine too.
            var cur = S.pick[i], cg = cur && S.gens[cur.gid], rf = refusedIn(cg, i);
            var alt = rf ? altEngine(cg.engine) : (refusingEngines(i).indexOf(S.engine) >= 0 ? altEngine(S.engine) : null);
            if (rf) {
                var rn = document.createElement('div'); rn.className = 'vs-sc-refused';
                // The video's own clock, as in the result's notice and the selection line.
                rn.textContent = '⚠ ' + fmt(T.scene_refused, { engine: engineName(cg.engine), from: clock(rf.start), to: clock(rf.end) });
                main.appendChild(rn);
            }
            if (S.gensCount() && S.enabled && S.authed) {
                var rv = document.createElement('button'); rv.type = 'button'; rv.className = 'vs-link';
                if (alt) {
                    rv.textContent = fmt(T.scene_refused_btn, { engine: engineName(alt), price: scenePrice(i, alt) });
                    rv.addEventListener('click', function () {
                        if (S.busy) return;
                        ae('refused_fix', { from: cg.engine, to: alt });
                        startGeneration({ start: S.scenes[i].start, end: S.scenes[i].end }, 'scene', alt);
                    });
                } else {
                    var pr = scenePrice(i);
                    rv.textContent = pr ? fmt(T.scene_revoice_fmt, { price: pr }) : T.scene_revoice;
                    rv.addEventListener('click', function () { revoiceScene(i); });
                }
                rv.disabled = S.busy;
                row.appendChild(rv);
            }
            var cb = compareButton(i);
            if (cb) row.appendChild(cb);
            if (cb && s.end - s.start > LONG_SCENE_S) {
                var lh = document.createElement('div'); lh.className = 'vs-sc-loop'; lh.textContent = T.cmp_long_hint;
                main.appendChild(row); main.appendChild(lh); row = null;
            }
            if (row) main.appendChild(row);
            var cs = compareStatus(i);
            if (cs) main.appendChild(cs);
            var ap = applyEngineButton(i);
            if (ap) main.appendChild(ap);
            if (S.loop && S.loop.i === i && !el.video.paused) {
                var lp = document.createElement('div'); lp.className = 'vs-sc-loop';
                lp.textContent = '🔁 ' + (coarse() ? T.cmp_loop_touch : fmt(T.cmp_loop, { n: Math.min(9, takes.length) }));
                main.appendChild(lp);
            }
            li.appendChild(thumb); li.appendChild(main);
            el.scenes.appendChild(li);
        });
        renderPeopleHint();
    }
    S.gensCount = function () { return Object.keys(S.gens).filter(function (k) { return S.gens[k].takes && Object.keys(S.gens[k].takes).length; }).length; };

    function mergeScene(i) {
        if (i < 1 || !S.scenes[i]) return;
        var a = S.scenes[i - 1], b = S.scenes[i];
        a.end = b.end;
        if (!a.text && b.text) { a.text = b.text; a.en = b.en; }
        a.people = a.people || b.people;
        S.scenes.splice(i, 1); S.pick.splice(i, 1); S.hand.splice(i, 1);
        renderScenes(); renderTimeline(); renderPrice(); rebuildTrack();
        ae('merge', {});
    }
    el.split.addEventListener('click', function () {
        if (!S.span) return;
        var t = el.video.currentTime - S.span.start;
        for (var i = 0; i < S.scenes.length; i++) {
            var s = S.scenes[i];
            if (t > s.start + 1 && t < s.end - 1) {
                S.scenes.splice(i + 1, 0, { start: Math.round(t * 1000) / 1000, end: s.end, text: s.text, en: s.en, people: s.people, edited: s.edited });
                s.end = Math.round(t * 1000) / 1000;
                S.pick.splice(i + 1, 0, S.pick[i]); S.hand.splice(i + 1, 0, S.hand[i]);
                renderScenes(); renderTimeline(); renderPrice(); ae('split', {});
                return;
            }
        }
        toast(T.split_bad, 'warning');
    });
    el.selAll.addEventListener('click', function () { if (!S.span) return; S.sel = { start: 0, end: S.span.end - S.span.start }; tl.set({ sel: S.sel }); renderSelInfo(); renderPrice(); renderScenes(); });

    function renderSelInfo() {
        if (!S.sel) { el.selInfo.textContent = ''; return; }
        var n = S.scenes.filter(function (s) { return s.end > S.sel.start + 0.01 && s.start < S.sel.end - 0.01; }).length;
        el.selInfo.textContent = fmt(T.sel_info, { from: clockD((S.span ? S.span.start : 0) + S.sel.start), to: clockD((S.span ? S.span.start : 0) + S.sel.end), len: clock(S.sel.end - S.sel.start), n: n || 1 });
        el.selAll.hidden = !S.span || (S.sel.start < 0.02 && S.sel.end > S.span.end - S.span.start - 0.02);
    }

    // ── engines / options / price ────────────────────────────────────────────
    /* Measured 2026-09-24 (AudioSet tagger over the bake-off): with people in
       the shot Kling and Mirelo add unintelligible voices whatever the prompt
       says, Sonilo does not. Say so exactly where the choice is made. */
    function renderPeopleHint() {
        var hint = $('vsPeopleHint');
        if (!hint) return;
        var people = S.sel && S.scenes.some(function (s) { return s.people && s.end > S.sel.start + 0.01 && s.start < S.sel.end - 0.01; });
        hint.hidden = !(people && S.engine !== 'sonilo');
    }
    function renderEngines() {
        [].forEach.call(el.engines.querySelectorAll('.vs-eng'), function (b) {
            var on = b.getAttribute('data-engine') === S.engine;
            b.classList.toggle('on', on); b.setAttribute('aria-checked', on ? 'true' : 'false');
            b.tabIndex = on ? 0 : -1;
        });
        el.asmrRow.hidden = !(S.shape && S.shape.engines[S.engine] && S.shape.engines[S.engine].supports && S.shape.engines[S.engine].supports.asmr);
        renderPeopleHint();
    }
    el.engines.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('.vs-eng') : null;
        if (!b) return;
        S.engine = b.getAttribute('data-engine'); saveSet('engine', S.engine);
        renderEngines(); renderVariants(); renderPrice(); renderScenes();
        ae('engine', { engine: S.engine });
    });
    el.engines.addEventListener('keydown', function (e) {
        if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].indexOf(e.key) < 0) return;
        e.preventDefault();
        var list = [].slice.call(el.engines.querySelectorAll('.vs-eng')), i = list.findIndex(function (b) { return b.classList.contains('on'); });
        var n = list[(i + (e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : list.length - 1)) % list.length];
        n.click(); n.focus();
    });
    function renderVariants() {
        if (!S.shape) return;
        var e = S.shape.engines[S.engine], cur = S.variants[S.engine];
        el.variants.innerHTML = '';
        (e.variants || [1]).forEach(function (v) {
            var b = document.createElement('button'); b.type = 'button'; b.className = 'tc-seg-b' + (v === cur ? ' on' : '');
            b.setAttribute('role', 'radio'); b.setAttribute('aria-checked', v === cur ? 'true' : 'false');
            b.textContent = String(v);
            b.addEventListener('click', function () { S.variants[S.engine] = v; renderVariants(); renderPrice(); ae('variants', { v: v }); });
            el.variants.appendChild(b);
        });
    }
    function renderOptions() {
        el.noVoices.checked = S.opts.no_voices;
        el.music.checked = S.opts.music;
        el.musicRow.hidden = !S.opts.music;
        var mus = S.shape && S.shape.music && S.shape.music.enabled;
        el.music.closest('.vs-sw').hidden = !mus;
    }
    el.noVoices.addEventListener('change', function () { S.opts.no_voices = el.noVoices.checked; saveSet('no_voices', S.opts.no_voices); ae('opt', { k: 'no_voices', v: S.opts.no_voices ? 1 : 0 }); });
    el.asmr.addEventListener('change', function () { S.opts.asmr = el.asmr.checked; ae('opt', { k: 'asmr', v: S.opts.asmr ? 1 : 0 }); });
    el.music.addEventListener('change', function () { S.opts.music = el.music.checked; el.musicRow.hidden = !S.opts.music; renderPrice(); ae('opt', { k: 'music', v: S.opts.music ? 1 : 0 }); });
    el.musicPrompt.addEventListener('input', function () { S.opts.music_prompt = el.musicPrompt.value; S.opts.music_prompt_en = ''; });

    function scenesInSel(sel) {
        var out = [];
        S.scenes.forEach(function (s) {
            var a = Math.max(s.start, sel.start), b = Math.min(s.end, sel.end);
            if (b - a > 0.01) out.push({ start: a - sel.start, end: b - sel.start, src: s });
        });
        return out;
    }
    function planFor(engine, sel, variants, music) {
        if (!S.shape || !sel) return null;
        var sc = scenesInSel(sel);
        return window.VideoSoundPlan.make(S.shape, engine, sel.end - sel.start, sc.map(function (s) { return { start: s.start }; }), variants, music);
    }
    /* The examples' price in money too, as on the engine cards — once the rate
       is known; each card prices the engine picked on it (exampleEngines). */
    function exampleMoney() { exampleEngines(); }
    function money(n) { try { return window.__tokenPrice ? window.__tokenPrice.money(n) : ''; } catch (_) { return ''; } }
    function tokensTxt(n) { return fmt(T.price_fmt, { n: String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') }); }
    function currentPlan() { return S.sel ? planFor(S.engine, S.sel, S.variants[S.engine] || 1, S.opts.music) : null; }
    function renderPrice() {
        if (!S.shape) return;
        // Per-engine price on each card, for the current selection.
        [].forEach.call(el.engines.querySelectorAll('[data-price-for]'), function (em) {
            var e = em.getAttribute('data-price-for');
            var p = S.sel ? planFor(e, S.sel, S.variants[e] || S.shape.engines[e].default_variants || 1, false) : null;
            em.textContent = p ? fmt(T.engine_price_fmt, { price: tokensTxt(p.tokens) + (money(p.tokens) ? ' ' + money(p.tokens) : '') }) : '';
        });
        var p = currentPlan();
        if (!p) { el.price.textContent = ''; el.rub.textContent = ''; el.priceNote.textContent = ''; renderGate(); return; }
        el.price.textContent = tokensTxt(p.tokens);
        el.rub.textContent = money(p.tokens);
        el.priceNote.textContent = S.opts.music ? T.price_note_music : '';
        renderGate();
        renderCmpRail();
    }
    function scenePrice(i, engine) {
        var s = S.scenes[i], e = engine || S.engine;
        if (!s || !S.shape || !S.shape.engines[e]) return '';
        var p = planFor(e, { start: s.start, end: s.end }, S.variants[e] || S.shape.engines[e].default_variants || 1, false);
        return p ? tokensTxt(p.tokens) : '';
    }
    function renderBalance() {
        if (!S.authed) { el.balance.textContent = ''; el.topup.hidden = true; return; }
        el.balance.textContent = fmt(T.balance_fmt, { n: String(S.balance || 0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') });
        if (cfg.storeUrl) { el.topup.hidden = false; el.topup.href = cfg.storeUrl; el.topup.textContent = T.topup; }
    }
    function gateText(msg, html) {
        el.gate.hidden = !msg; if (html) el.gate.innerHTML = msg; else el.gate.textContent = msg || '';
    }
    function renderGate() {
        var p = currentPlan();
        var why = '';
        if (!S.enabled) why = T.disabled_msg;
        else if (!S.file) why = '';
        else if (!S.prep) why = S.pickingRange ? '' : T.need_prep;
        else if (!S.authed) { gateText('<a href="' + esc(cfg.registerUrl) + '">' + esc(T.login_cta) + '</a>', true); el.go.disabled = true; return; }
        else if (p && typeof S.balance === 'number' && S.balance < p.tokens) {
            var short = p.tokens - S.balance;
            el.gate.hidden = false; el.gate.textContent = '';
            var sp = document.createElement('span'); sp.textContent = fmt(T.need_more_fmt, { n: short }); el.gate.appendChild(sp);
            if (cfg.storeUrl) { var a = document.createElement('a'); a.href = cfg.storeUrl; a.textContent = T.topup; a.setAttribute('data-tmb-short', String(short)); el.gate.appendChild(a); }
            el.go.disabled = true; return;
        }
        gateText(why);
        el.go.disabled = !!why || !S.prep || S.busy || !S.enabled || !S.authed;
        el.go.textContent = (S.busy ? '⏳ ' + T.generating : '🔊 ' + T.generate);
    }

    // ── generate ──────────────────────────────────────────────────────────────
    el.go.addEventListener('click', function () { if (!el.go.disabled && S.sel) startGeneration(S.sel, 'all'); });

    async function ensureUpload() {
        var key = S.span.start + ':' + S.span.end + ':' + (S.file && S.file.size);
        if (S.uploadId && S.uploadSpanKey === key) return S.uploadId;
        // Over the server's ceiling it would be refused AFTER the whole upload —
        // on 3G that was minutes per try, and the old words blamed the connection.
        var capMb = +cfg.uploadMaxMb || 0;
        if (capMb && S.prep.proxy.size > capMb * 1048576) {
            ae('proxy_too_large', { kb: Math.round(S.prep.proxy.size / 1024), cap: capMb });
            var big = new Error(fmt(T.err_upload_too_large, { n: capMb })); big.status = 413; throw big;
        }
        progress('upload', 0, fmt(T.upload_pct, { n: 0 }));
        var fd = new FormData();
        fd.append('video', new File([S.prep.proxy], 'proxy.mp4', { type: 'video/mp4' }));
        fd.append('locale', cfg.locale);
        // The proxy goes AHEAD in slices sized by the link (window.__uploadBody,
        // /api/upload-slice): one long POST of it is what a phone's uplink loses —
        // RU nginx, 14 days to 07.10.2026: 6 of 29 uploads here that ran ≥ 20 s
        // were cut by the client. The request below then carries only its name.
        var B = window.__uploadBody;
        var bar = function (sent, total) { if (total) progress('upload', sent / total, fmt(T.upload_pct, { n: Math.round(100 * sent / total) })); };
        var post = function (body) {
            var sliced = !!(B && B.sliced && B.sliced(body));
            return new Promise(function (resolve) {
                var x = new XMLHttpRequest();
                x.open('POST', q(cfg.api.upload));
                x.setRequestHeader('X-CSRF-TOKEN', csrf()); x.setRequestHeader('X-Requested-With', 'XMLHttpRequest'); x.setRequestHeader('Accept', 'application/json');
                x.withCredentials = true;
                x.upload.onprogress = function (e) { if (!sliced && e.lengthComputable) bar(e.loaded, e.total); };
                x.onload = function () { var j = {}; try { j = JSON.parse(x.responseText); } catch (_) {} resolve({ status: x.status, json: j, text: x.responseText }); };
                x.onerror = function () { resolve({ status: 0, json: {} }); };
                x.send(body);
            });
        };
        var build = function (fresh) {
            if (typeof B !== 'function') return Promise.resolve(fd);
            return B(fd, { slices: true, fresh: fresh, onProgress: bar }).catch(function (err) {
                var e0 = new Error((B.text && B.text(err)) || T.err_upload); e0.status = 0; throw e0;
            });
        };
        var res = await post(await build(false));
        // the server forgot the proxy sent ahead (answered before any work): once more
        if (B && B.gone && B.gone(res.status, res.text)) res = await post(await build(true));
        if (res.status !== 200 || !res.json.ok) {
            var e = new Error(res.json.msg || (res.status === 413 ? fmt(T.err_upload_too_large, { n: +cfg.uploadMaxMb || 80 }) : T.err_upload));
            e.status = res.status; throw e;
        }
        S.uploadId = res.json.id; S.uploadSpanKey = key;
        ae('uploaded', { kb: Math.round(S.prep.proxy.size / 1024) });
        return S.uploadId;
    }

    /* `engine` — this order's engine when it is not the one picked on the page:
       a stretch the picked engine refused is offered to another one. */
    async function startGeneration(sel, kind, engine) {
        if (S.busy) return;
        var eng = engine || S.engine;
        S.busy = true; renderGate(); renderScenes();
        el.err.hidden = true;
        var sc = scenesInSel(sel);
        sc.forEach(function (x) { x.src.pending = true; });
        renderTimeline();
        S.startedAt = Date.now();
        el.prog.hidden = false;
        stage('generating');
        try {
            await ensureUpload();
            var vars = S.variants[eng] || (S.shape && S.shape.engines[eng] && S.shape.engines[eng].default_variants) || 1;
            var r = await order(sel, eng, vars, S.opts.music && kind === 'all', kind);
            S.gid = r.id; S.attached = false;
            try { if (window.__unsavedWork) window.__unsavedWork(true); } catch (_) {}
            poll(S.gid);
        } catch (e) {
            finishBusy();
            sc.forEach(function (x) { x.src.pending = false; });
            renderTimeline();
            el.prog.hidden = true;
            showError(e.userMsg || e.message || T.err_generic);
            ae('generate_failed', { status: e.status || 0 });
        }
    }
    /* One order to the server for `sel` (span-relative) — the whole range, one
       scene, or one engine of a comparison. Resolves {id, cost_tokens}. */
    async function order(sel, eng, vars, music, kind) {
        var sc = scenesInSel(sel);
        var p = planFor(eng, sel, vars, music);
        var body = {
            locale: cfg.locale, upload_id: S.uploadId, engine: eng, variants: vars, kind: kind,
            span: { start: sel.start, end: sel.end },
            range: { start: S.span.start + sel.start, end: S.span.start + sel.end },
            scenes: sc.map(function (x) { return { start: x.start, prompt: x.src.text || '', prompt_en: x.src.edited ? '' : (x.src.en || '') }; }),
            options: { no_voices: S.opts.no_voices, asmr: S.opts.asmr, music: !!music, music_prompt: S.opts.music_prompt || '', music_prompt_en: S.opts.music_prompt_en || '' },
            source: { name: S.file.name, duration: S.meta.duration, width: S.meta.width, height: S.meta.height, has_audio: S.hasAudio }
        };
        var r = await api(q(cfg.api.generate), { method: 'POST', body: body });
        if (r.status !== 200 || !r.json.ok) throw httpError(r);
        if (typeof r.json.balance === 'number') { S.balance = r.json.balance; renderBalance(); }
        ae('generate', { engine: eng, v: body.variants, sec: Math.round(sel.end - sel.start), scenes: sc.length, music: body.options.music ? 1 : 0, tokens: r.json.cost_tokens, kind: kind, planned: p ? p.tokens : 0 });
        return { id: r.json.id, cost_tokens: r.json.cost_tokens };
    }
    function httpError(r) {
        var e = new Error(), j = r.json || {};
        e.status = r.status;
        if (r.status === 402) e.userMsg = fmt(T.err_insufficient, { n: j.need || '' });
        else if (r.status === 429) e.userMsg = T.err_throttled;
        else if (r.status === 401) { S.authed = false; e.userMsg = T.login_cta; }
        else e.userMsg = j.msg || T.err_generic;
        return e;
    }
    function finishBusy() { S.busy = false; renderGate(); renderScenes(); renderCmpRail(); try { if (window.__unsavedWork) window.__unsavedWork(false); } catch (_) {} }

    function progress(step, ratio, text) {
        // A comparison has no progress panel: its upload is told in the scene row.
        if (step === 'upload' && S.cmp && S.cmp.live && !S.gid) {
            var pc = Math.max(0, Math.min(0.999, ratio || 0));
            if (S.cmp.upload == null || Math.round(pc * 100) !== Math.round(S.cmp.upload * 100)) { S.cmp.upload = pc; renderScenes(); }
            return;
        }
        var steps = ['upload', 'queue', 'generate', 'mix'], idx = steps.indexOf(step);
        [].forEach.call(el.prog.querySelectorAll('.vs-step'), function (d) {
            var i = steps.indexOf(d.getAttribute('data-step'));
            d.classList.toggle('on', i === idx); d.classList.toggle('ok', i < idx);
        });
        el.progBar.style.width = Math.round(Math.max(0, Math.min(1, ratio || 0)) * 100) + '%';
        if (text != null) el.progText.textContent = text;
        el.progTime.textContent = fmt(T.elapsed_fmt, { s: clock((Date.now() - S.startedAt) / 1000) });
    }
    function stopPoll() { if (S.pollTimer) { clearTimeout(S.pollTimer); S.pollTimer = null; } }

    /* Remaining time — the shared estimator (auto-captions-eta.js): a number
       only once parts have actually come back (≥ 3 % and ≥ 5 s of evidence),
       counting down between answers instead of growing while one is slow. */
    function etaFor(d) {
        var A = window.__acETA;
        if (!A || !P || d.phase === 'assembling') return null;
        var key = 'vs-gen-' + d.id;
        if (S.etaKey !== key) { endEta(); A.beginStage(key); S.etaKey = key; }
        A.updateRate(key, P.fraction(d.progress));
        return A.remainingSec(key);
    }
    function endEta() { try { if (S.etaKey && window.__acETA) window.__acETA.endStage(S.etaKey); } catch (_) {} S.etaKey = null; }

    /* The same words in the «My video sounds» row: it is on screen with or
       without a video loaded, which is exactly the state after a reload. */
    function renderLive(d, eta) {
        var live = S.histLive[d.id];
        if (!live || !P) return;
        var txt = P.line(d.progress, T, eta);
        live.textContent = txt ? ' · ' + txt : '';
    }

    function poll(id) {
        stopPoll();
        api(q(cfg.api.status + '/' + id)).then(function (r) {
            if (S.gid !== id) return;
            var d = r.json || {};
            if (r.status !== 200) { S.pollTimer = setTimeout(function () { poll(id); }, 4000); return; }
            if (d.status === 'done') { onDone(d); return; }
            if (d.status === 'failed') { onFailed(d); return; }
            var pr = d.progress || {};
            // The server's clock: a page that attached after a reload continues
            // the count from the order instead of starting over at 0:00.
            if (typeof pr.elapsed_s === 'number') S.startedAt = Date.now() - pr.elapsed_s * 1000;
            S.lastStatus = d;
            var eta = etaFor(d);
            var etaTxt = P ? P.etaText(eta, T) : '';
            var parts = pr.total > 1 ? fmt(T.progress_fmt, { done: pr.done, total: pr.total }) : '';
            var withEta = [parts, etaTxt].filter(Boolean).join(' · ');
            if (d.phase === 'assembling') progress('mix', 0.95, '');
            else if (pr.done > 0 || pr.running > 0 || (pr.queue === 0 && d.phase === 'generating')) progress('generate', pr.total ? 0.1 + 0.8 * pr.done / pr.total : 0.3, withEta);
            else if (typeof pr.queue === 'number' && pr.queue > 0) progress('queue', 0.08, P ? P.queueText(pr, T) : fmt(T.queue_fmt, { n: pr.queue }));
            else progress('queue', 0.05, withEta);
            renderLive(d, eta);
            S.pollTimer = setTimeout(function () { poll(id); }, 2500);
        }).catch(function () { S.pollTimer = setTimeout(function () { poll(id); }, 4000); });
    }

    /* Follow a generation this page did not start: found running in the
       history after a reload, or asked for by ?gen=. The progress panel lives
       in the editor (shown once a video is loaded); the history row carries
       the same line meanwhile. */
    function attach(d) {
        if (!d || S.gid === d.id) return;
        stopPoll();
        S.gid = d.id; S.attached = true; S.busy = true;
        var pr = d.progress || {};
        S.startedAt = Date.now() - (typeof pr.elapsed_s === 'number' ? pr.elapsed_s * 1000 : 0);
        el.prog.hidden = false;
        renderGate();
        ae('reattach', { status: d.status, done: pr.done || 0, total: pr.total || 0, el: pr.elapsed_s || 0 });
        poll(d.id);
    }

    function cancelGeneration(id) {
        if (!id) return;
        api(q(cfg.api.cancel + '/' + id), { method: 'POST', body: {} }).then(function (r) {
            if (r.status === 200) {
                var followed = S.gid === id;
                if (followed) {
                    stopPoll(); endEta(); S.gid = null; S.attached = false; el.prog.hidden = true;
                    S.scenes.forEach(function (s) { s.pending = false; });
                    finishBusy(); renderTimeline();
                }
                if (typeof r.json.balance === 'number') { S.balance = r.json.balance; renderBalance(); }
                toast(T.cancelled, 'info');
                ae('cancel', { from: followed && !el.work.hidden ? 'panel' : 'history' });
                loadHistory();
            }
        });
    }
    el.cancel.addEventListener('click', function () { if (S.gid) cancelGeneration(S.gid); });
    function onFailed(d) {
        stopPoll(); endEta(); S.gid = null; S.attached = false; el.prog.hidden = true;
        S.scenes.forEach(function (s) { s.pending = false; });
        finishBusy(); renderTimeline();
        showError(d.msg || T.err_failed_refunded);
        loadBalance();
        ae('gen_failed', { error: d.error || '' });
        loadHistory();
    }
    function showError(msg) { el.err.hidden = false; el.errMsg.textContent = msg; }
    function loadBalance() { api(q(cfg.api.bootstrap)).then(function (r) { if (r.status === 200) { S.balance = r.json.balance; renderBalance(); renderGate(); } }); }

    async function onDone(d) {
        stopPoll(); endEta();
        var sec = (d.progress && typeof d.progress.elapsed_s === 'number') ? d.progress.elapsed_s : Math.round((Date.now() - S.startedAt) / 1000);
        if (S.attached || !S.span) {
            // Not ordered for the video now on the page (or no video at all):
            // announce it and let the history row open or apply it. The same
            // video, chosen later, gets it laid on by itself (pendingGen).
            el.prog.hidden = true; S.gid = null; S.attached = false;
            S.scenes.forEach(function (s) { s.pending = false; });
            finishBusy(); renderTimeline();
            if (!S.span) S.pendingGen = d;
            toast(T.result_title, 'success');
            ae('gen_done', { engine: d.engine, takes: (d.variants || []).length, sec: sec, attached: 1 });
            loadHistory();
            return;
        }
        progress('mix', 0.97, '');
        try {
            await absorb(d, true);
            el.prog.hidden = true;
            S.gid = null;
            S.scenes.forEach(function (s) { s.pending = false; });
            finishBusy();
            rebuildTrack(); renderScenes(); renderTimeline(); renderResult();
            setListen('ai');
            try { el.video.currentTime = Math.max(0, S.span.start + ((d.range && d.range.start != null) ? d.range.start - S.span.start : 0)); } catch (_) {}
            ae('gen_done', { engine: d.engine, takes: (d.variants || []).length, sec: Math.round((Date.now() - S.startedAt) / 1000), refused: (d.refused || []).length });
            stage('generated');
            loadHistory();
        } catch (e) {
            el.prog.hidden = true; S.gid = null; finishBusy();
            showError(T.err_generic);
            ae('absorb_failed', { m: String(e && e.message).slice(0, 80) });
        }
    }

    /* Load a finished generation's takes (light MP3 previews) and assign them to
       the scenes they cover. */
    /* assign: true — the order's first take goes on every scene it covers,
       except where the person chose a take by hand and the order spans more
       than that scene (an order of the whole range after a comparison must not
       undo the comparison); 'empty' — only on scenes with nothing yet (the
       engines of a comparison arrive one by one while the first is heard). */
    async function absorb(d, assign) {
        var g = { id: d.id, engine: d.engine, range: d.range, takes: {}, music: null, files: [], variants: d.variants || [],
                  refused: d.refused || [], notice: d.notice || '' };
        for (var i = 0; i < g.variants.length; i++) {
            var v = g.variants[i];
            var buf = await window.VideoSoundMixer.decode(v.src);
            if (v.kind === 'music') g.music = buf; else g.takes[v.n] = buf;
        }
        S.gens[d.id] = g;
        if (assign && S.span && g.range) {
            var span = 0;
            S.scenes.forEach(function (s) { var a = S.span.start + s.start, b = S.span.start + s.end; if (a >= g.range.start - 0.05 && b <= g.range.end + 0.05) span++; });
            S.scenes.forEach(function (s, i) {
                var a = S.span.start + s.start, b = S.span.start + s.end;
                if (a < g.range.start - 0.05 || b > g.range.end + 0.05) return;
                if (assign === 'empty' && S.pick[i]) return;
                if (S.hand[i] && span > 1 && S.pick[i] && S.gens[S.pick[i].gid]) return;
                // A scene this engine refused keeps the take it already had —
                // a re-order of the range must not replace sound with silence.
                if (refusedIn(g, i) && S.pick[i] && S.pick[i].gid !== d.id && S.gens[S.pick[i].gid]) return;
                S.pick[i] = { gid: d.id, n: firstTake(g) };
                if (span <= 1) S.hand[i] = false;
            });
            if (g.music) S.musicGid = d.id;
        }
        return g;
    }
    function firstTake(g) { var ks = Object.keys(g.takes).map(Number).sort(function (a, b) { return a - b; }); return ks[0] || 1; }
    /* The stretch of scene i (seconds of the video) that generation g's engine
       refused to voice — silent in its takes — or null. */
    function refusedIn(g, i) {
        var s = S.scenes[i];
        if (!g || !s || !S.span || !(g.refused && g.refused.length)) return null;
        var a = S.span.start + s.start, b = S.span.start + s.end;
        for (var k = 0; k < g.refused.length; k++) {
            var r = g.refused[k];
            if (Math.min(b, r.end) - Math.max(a, r.start) >= 0.5) return r;
        }
        return null;
    }
    /* Engines whose generations on this page refused (part of) scene i. */
    function refusingEngines(i) {
        var out = [];
        Object.keys(S.gens).forEach(function (gid) {
            var g = S.gens[gid];
            if (refusedIn(g, i) && out.indexOf(g.engine) < 0) out.push(g.engine);
        });
        return out;
    }
    function engineName(e) { return String(T['engine_' + e] || e).split('·')[0].trim(); }
    /* Where a refused stretch goes: «Подробный» (one call over the whole stretch,
       and the engine that took this very footage on 2026-10-03), unless it was
       the one refusing. */
    function altEngine(refusing) {
        var ks = S.shape ? Object.keys(S.shape.engines) : [];
        if (refusing !== 'sonilo' && ks.indexOf('sonilo') >= 0) return 'sonilo';
        for (var k = 0; k < ks.length; k++) if (ks[k] !== refusing) return ks[k];
        return null;
    }
    function takesFor(i) {
        var s = S.scenes[i], out = [];
        if (!s || !S.span) return out;
        var a = S.span.start + s.start, b = S.span.start + s.end;
        Object.keys(S.gens).forEach(function (gid) {
            var g = S.gens[gid];
            if (!g.range || a < g.range.start - 0.05 || b > g.range.end + 0.05) return;
            // A take the engine left silent over (nearly) the whole scene is not a take to choose.
            var r = refusedIn(g, i);
            if (r && Math.min(b, r.end) - Math.max(a, r.start) >= 0.9 * (b - a)) return;
            Object.keys(g.takes).map(Number).sort(function (x, y) { return x - y; }).forEach(function (n) { out.push({ gid: Number(gid), n: n }); });
        });
        return VC ? VC.sortTakes(out, gensList()) : out;
    }
    function gensList() { return Object.keys(S.gens).map(function (k) { return S.gens[k]; }); }
    /* One name per take for the whole page (compare.js): the scene row, «for all scenes», the file list. */
    function takeLabels() { return VC ? VC.labels(gensList(), engineName) : {}; }
    function takeName(gid, n) { var l = takeLabels()[gid + ':' + n]; return l ? l.label : fmt(T.take_fmt, { n: n }); }

    /* The person picks a take for scene i. Paused: the scene starts and loops,
       so the takes can be compared by ear; playing: the sound is swapped in
       place (mixer.js cross-fades), the picture goes on. */
    function pickTake(i, t, k, how) {
        var g = S.gens[t.gid];
        S.pick[i] = { gid: t.gid, n: t.n };
        S.hand[i] = true;
        rebuildTrack(); renderScenes(); renderTimeline(); renderResult();
        if (el.video.paused && S.previewOk && how === 'chip') loopScene(i);
        ae('take_scene', { k: k, e: g ? g.engine : '', n: takesFor(i).length, how: how, cmp: S.cmp && S.cmp.i === i ? 1 : 0 });
    }
    function loopScene(i) {
        var s = S.scenes[i]; if (!s || !S.span) return;
        S.stopAt = null;
        S.loop = { i: i, a: S.span.start + s.start, b: S.span.start + s.end };
        seekTo(S.loop.a + 0.01);
        setListen('ai', true);
        try { window.VideoSoundMixer.ctx().resume(); } catch (_) {}
        el.video.play().catch(function () {});
        loopTick();
    }
    /* Checked every frame, not on timeupdate (4 Hz): a quarter-second past the
       scene's end is the NEXT scene's sound — exactly what is not compared. */
    function loopTick() {
        if (S.loopRaf) return;
        var tick = function () {
            S.loopRaf = null;
            var L = S.loop;
            if (!L || el.video.paused) return;
            var t = el.video.currentTime;
            if (t < L.a - 0.3 || t > L.b + 0.5) { S.loop = null; renderScenes(); return; }   // moved elsewhere by hand
            if (t >= L.b - 0.03) seekTo(L.a + 0.01);
            S.loopRaf = requestAnimationFrame(tick);
        };
        S.loopRaf = requestAnimationFrame(tick);
    }
    function coarse() { try { return window.matchMedia('(pointer: coarse)').matches && !window.matchMedia('(any-pointer: fine)').matches; } catch (_) { return false; } }
    /* A comparison costs every engine's price for the whole scene. Measured on
       both shards 29.10.2026 (1 204 scenes of real orders): median 5.8 s, 85 %
       ≤ 10 s — but one in seven is longer, up to 41 s. */
    var LONG_SCENE_S = 10;
    function sceneAt(t) {
        if (!S.span) return -1;
        var r = t - S.span.start;
        for (var i = 0; i < S.scenes.length; i++) if (r >= S.scenes[i].start - 0.01 && r < S.scenes[i].end) return i;
        return S.scenes.length ? S.scenes.length - 1 : -1;
    }

    // ── compare the engines on one scene ─────────────────────────────────────
    function engineList() { return S.shape ? VC.ENGINES.filter(function (e) { return !!S.shape.engines[e]; }) : []; }
    function defaultVariants(e) { return (S.shape && S.shape.engines[e] && S.shape.engines[e].default_variants) || 1; }
    function haveEngines(i) {
        var out = [];
        takesFor(i).forEach(function (t) { var g = S.gens[t.gid]; if (g && out.indexOf(g.engine) < 0) out.push(g.engine); });
        return out;
    }
    /* What comparing scene i would order: the engines with no take there yet,
       each with its own default number of takes (Студия — both), no music. */
    function comparePlan(i) {
        var s = S.scenes[i], out = { engines: [], per: {}, total: 0 };
        if (!s || !VC || !S.shape) return out;
        out.engines = VC.toOrder(haveEngines(i), engineList());
        out.engines.forEach(function (e) {
            var p = planFor(e, { start: s.start, end: s.end }, defaultVariants(e), false);
            out.per[e] = p ? p.tokens : 0; out.total += out.per[e];
        });
        return out;
    }
    function canCompare(i) {
        if (!VC || !S.enabled || !S.authed || !S.prep || !S.scenes[i]) return false;
        var p = comparePlan(i);
        return p.engines.length > 0 && p.engines.length + haveEngines(i).length >= 2;
    }
    function compareButton(i) {
        if (!canCompare(i) || (S.cmp && S.cmp.live)) return null;
        var p = comparePlan(i);
        var b = document.createElement('button'); b.type = 'button'; b.className = 'vs-link vs-cmp-btn';
        b.textContent = '⚖ ' + fmt(haveEngines(i).length ? T.cmp_more_fmt : T.cmp_btn_fmt, { price: tokensTxt(p.total) });
        b.title = T.cmp_hint;
        b.disabled = S.busy;
        b.addEventListener('click', function () { startCompare(i, 'scene'); });
        return b;
    }
    function compareStatus(i) {
        var C = S.cmp;
        if (!C || C.i !== i) return null;
        var box = document.createElement('div'); box.className = 'vs-cmp-st'; box.setAttribute('role', 'status');
        if (C.need) {
            box.textContent = fmt(T.need_more_fmt, { n: C.need });
            if (cfg.storeUrl) { var a = document.createElement('a'); a.href = cfg.storeUrl; a.textContent = ' ' + T.topup; a.setAttribute('data-tmb-short', String(C.need)); box.appendChild(a); }
            return box;
        }
        if (C.live && C.upload != null && C.upload < 1) { box.textContent = '⚖ ' + fmt(T.upload_pct, { n: Math.round(C.upload * 100) }); return box; }
        var parts = VC.statusParts(C.runs, T, engineName).filter(function (p) { return C.live || p.st === 'refused' || p.st === 'failed'; });
        if (!parts.length) return null;
        parts.forEach(function (p) {
            var sp = document.createElement('span'); sp.className = 'vs-cmp-run is-' + p.st;
            sp.textContent = p.text;
            var r = C.runs[p.engine];
            if (r && r.msg) sp.title = r.msg;
            box.appendChild(sp);
        });
        if (C.live) {
            var cx = document.createElement('button'); cx.type = 'button'; cx.className = 'vs-link'; cx.textContent = T.cancel;
            cx.addEventListener('click', cancelCompare);
            box.appendChild(cx);
        }
        return box;
    }
    /* After a comparison: the take heard on the scene names an engine — one
       press orders the whole selection with it (the price on the button). */
    function applyEngineButton(i) {
        if (!S.enabled || !S.authed || S.busy || !S.sel) return null;
        var cur = S.pick[i], g = cur && S.gens[cur.gid];
        if (!g || haveEngines(i).length < 2) return null;
        var inSel = scenesInSel(S.sel);
        if (inSel.length < 2) return null;
        // Already voiced with this engine over the selection — nothing to offer.
        var a = S.span.start + S.sel.start, b = S.span.start + S.sel.end;
        var done = gensList().some(function (x) { return x.engine === g.engine && x.range && x.range.start <= a + 0.05 && x.range.end >= b - 0.05; });
        if (done) return null;
        var p = planFor(g.engine, S.sel, S.variants[g.engine] || defaultVariants(g.engine), S.opts.music);
        var btn = document.createElement('button'); btn.type = 'button'; btn.className = 'tc-btn sm vs-cmp-apply';
        btn.textContent = '🔊 ' + fmt(T.cmp_apply_fmt, { engine: engineName(g.engine), price: p ? tokensTxt(p.tokens) : '' });
        btn.addEventListener('click', function () {
            if (S.busy) return;
            S.engine = g.engine; saveSet('engine', S.engine);
            renderEngines(); renderVariants(); renderPrice();
            ae('compare_apply', { engine: g.engine, scenes: inSel.length, tokens: p ? p.tokens : 0 });
            startGeneration(S.sel, 'all');
        });
        return btn;
    }
    /* The rail: compare where the playhead is (the scene looped, or the first
       scene of the selection that still lacks an engine). */
    function railScene() {
        if (!S.span || !S.scenes.length) return -1;
        var i = S.loop ? S.loop.i : sceneAt(el.video.currentTime || S.span.start);
        var len = function (k) { return S.scenes[k].end - S.scenes[k].start; };
        if (i >= 0 && canCompare(i) && len(i) <= LONG_SCENE_S) return i;
        // A long scene under the playhead: offer the shortest one of the selection
        // that is still long enough to hear (≥ 2 s) — the same answer, for less.
        var best = -1;
        for (var k = 0; k < S.scenes.length; k++) {
            var s = S.scenes[k];
            if (S.sel && !(s.end > S.sel.start + 0.01 && s.start < S.sel.end - 0.01)) continue;
            if (!canCompare(k)) continue;
            if (len(k) >= 2 && len(k) <= LONG_SCENE_S && (best < 0 || len(k) < len(best))) best = k;
        }
        if (best >= 0) return best;
        if (i >= 0 && canCompare(i)) return i;
        for (var j = 0; j < S.scenes.length; j++) if (canCompare(j)) return j;
        return -1;
    }
    function renderCmpRail() {
        if (!el.cmpRail) return;
        var i = (S.cmp && S.cmp.live) ? -1 : railScene();
        S.cmpRailI = i;
        el.cmpRail.hidden = i < 0; el.cmpRailNote.hidden = i < 0;
        if (i < 0) return;
        var p = comparePlan(i), s = S.scenes[i];
        el.cmpRail.textContent = '⚖ ' + fmt(T.cmp_rail_fmt, { n: i + 1, from: clockD(s.start), to: clockD(s.end), price: tokensTxt(p.total) });
        el.cmpRail.disabled = S.busy;
    }
    if (el.cmpRail) el.cmpRail.addEventListener('click', function () { if (S.cmpRailI >= 0) startCompare(S.cmpRailI, 'rail'); });

    async function startCompare(i, from) {
        if (S.busy || !canCompare(i)) return;
        var s = S.scenes[i], plan = comparePlan(i);
        if (typeof S.balance === 'number' && S.balance < plan.total) {
            S.cmp = { i: i, need: plan.total - S.balance, runs: {} };
            renderScenes(); ae('compare_short', { need: plan.total - S.balance });
            return;
        }
        var C = { i: i, start: s.start, end: s.end, runs: {}, t0: Date.now(), live: true, upload: null, timer: null, tokens: plan.total };
        plan.engines.forEach(function (e) { C.runs[e] = { st: 'send' }; });
        S.cmp = C; S.busy = true; s.pending = true;
        el.err.hidden = true;
        renderGate(); renderScenes(); renderTimeline(); renderCmpRail();
        ae('compare_start', { i: i, engines: plan.engines.join(','), have: haveEngines(i).length, tokens: plan.total, sec: Math.round(s.end - s.start), from: from });
        try { if (window.__unsavedWork) window.__unsavedWork(true); } catch (_) {}
        try {
            await ensureUpload();
        } catch (e) {
            C.upload = null; plan.engines.forEach(function (en) { C.runs[en] = { st: 'failed', msg: e.message || T.err_upload }; });
            finishCompare();
            showError(e.message || T.err_upload);
            return;
        }
        C.upload = 1;
        var sel = { start: s.start, end: s.end };
        // Three orders at once: one that hit a server hiccup (5xx, a dropped
        // link) is sent once more before it is reported as «did not work».
        var once = function (e) {
            return order(sel, e, defaultVariants(e), false, 'compare').catch(function (err) {
                if (err && err.status && err.status < 500) throw err;   // 402, 422, 429 — an answer, not a hiccup
                return new Promise(function (r) { setTimeout(r, 1500); }).then(function () { return order(sel, e, defaultVariants(e), false, 'compare'); });
            });
        };
        await Promise.all(plan.engines.map(function (e) {
            return once(e).then(function (r) {
                C.runs[e] = { st: 'queue', gid: r.id };
            }).catch(function (err) {
                C.runs[e] = { st: 'failed', msg: err.userMsg || err.message || T.err_generic };
            });
        }));
        if (S.cmp !== C) return;
        renderScenes();
        comparePoll(C);
    }
    function comparePoll(C) {
        if (S.cmp !== C || !C.live) return;
        var open = Object.keys(C.runs).filter(function (e) { return C.runs[e].gid && !VC.terminal(C.runs[e].st); });
        if (!open.length) { finishCompare(); return; }
        Promise.all(open.map(function (e) {
            var r = C.runs[e];
            return api(q(cfg.api.status + '/' + r.gid)).then(async function (res) {
                if (res.status !== 200 || S.cmp !== C) return;
                var d = res.json || {};
                if (d.status === 'done') {
                    try {
                        var g = await absorb(d, 'empty');
                        var silent = !takesFor(C.i).some(function (t) { return t.gid === g.id; });
                        C.runs[e] = Object.assign(VC.runState(d, silent), { gid: r.gid });
                        rebuildTrack(); renderTimeline(); renderResult();
                    } catch (_) { C.runs[e] = { st: 'failed', gid: r.gid, msg: T.err_generic }; }
                } else {
                    C.runs[e] = Object.assign(VC.runState(d, false), { gid: r.gid });
                }
            }).catch(function () {});
        })).then(function () {
            if (S.cmp !== C) return;
            renderScenes();
            C.timer = setTimeout(function () { comparePoll(C); }, 2500);
        });
    }
    function finishCompare() {
        var C = S.cmp;
        if (!C) return;
        if (C.timer) { clearTimeout(C.timer); C.timer = null; }
        C.live = false;
        if (S.scenes[C.i]) S.scenes[C.i].pending = false;
        var st = {}; Object.keys(C.runs).forEach(function (e) { st[e] = C.runs[e].st; });
        ae('compare_done', { i: C.i, ms: Date.now() - (C.t0 || Date.now()), st: JSON.stringify(st), takes: takesFor(C.i).length });
        finishBusy(); renderTimeline(); renderResult(); renderCmpRail();
        loadBalance(); loadHistory();
        if (!el.video.paused || !S.previewOk) return;
        // The takes are here: hear them at once, in a loop, on the scene compared.
        if (takesFor(C.i).length > 1) loopScene(C.i);
    }
    function cancelCompare() {
        var C = S.cmp;
        if (!C || !C.live) return;
        Object.keys(C.runs).forEach(function (e) {
            var r = C.runs[e];
            if (r.gid && !VC.terminal(r.st)) api(q(cfg.api.cancel + '/' + r.gid), { method: 'POST', body: {} }).catch(function () {});
            if (!VC.terminal(r.st)) C.runs[e] = { st: 'failed', gid: r.gid, msg: T.cancelled };
        });
        ae('compare_cancel', { i: C.i });
        toast(T.cancelled, 'info');
        finishCompare();
    }

    function rebuildTrack() {
        if (!S.span) return;
        var len = S.span.end - S.span.start;
        var any = S.pick.some(function (p) { return !!p; });
        if (!any) { S.aiTrack = null; S.musicTrack = null; player.setBuffers({}, S.span.start, S.span.end); listenAvail(); return; }
        S.aiTrack = window.VideoSoundMixer.buildTrack(len, S.scenes, function (i) {
            var p = S.pick[i]; if (!p) return null;
            var g = S.gens[p.gid]; if (!g || !g.takes[p.n]) return null;
            return { buf: g.takes[p.n], at: S.span.start - g.range.start };
        });
        S.musicTrack = null;
        var mg = S.musicGid && S.gens[S.musicGid];
        if (mg && mg.music) {
            var ms = Math.max(0, mg.range.start - S.span.start), me = Math.min(len, mg.range.end - S.span.start);
            S.musicTrack = window.VideoSoundMixer.buildTrack(len, [{ start: 0, end: ms }, { start: ms, end: me }, { start: me, end: len }], function (i) {
                return i === 1 ? { buf: mg.music, at: S.span.start - mg.range.start } : null;
            });
        }
        player.setBuffers({ ai: S.aiTrack, music: S.musicTrack }, S.span.start, S.span.end);
        el.gMusicRow.hidden = !S.musicTrack;
        listenAvail();
    }

    function revoiceScene(i) {
        var s = S.scenes[i];
        if (!s || S.busy) return;
        ae('revoice', { i: i, engine: S.engine });
        startGeneration({ start: s.start, end: s.end }, 'scene');
    }

    // ── result, mixer, export ─────────────────────────────────────────────────
    function renderResult() {
        el.result.hidden = !S.gensCount();
        if (el.result.hidden) return;
        el.takes.innerHTML = '';
        var last = S.gens[Object.keys(S.gens).map(Number).sort(function (a, b) { return b - a; })[0]];
        // The engine refused part of the latest order: which stretches are silent,
        // that they were not charged, where the way out is (the server's words).
        el.refused.hidden = !(last && last.notice);
        el.refused.textContent = (last && last.notice) || '';
        var whole = VC ? VC.wholeTakes(gensList(), function (g) {
            var c = 0; S.scenes.forEach(function (_, i) { if (takesFor(i).some(function (t) { return t.gid === g.id; })) c++; }); return c;
        }, S.scenes.length) : [];
        if (whole.length > 1) {
            var names = takeLabels();
            var lab = document.createElement('span'); lab.textContent = T.take_all; el.takes.appendChild(lab);
            el.takes.setAttribute('role', 'radiogroup'); el.takes.setAttribute('aria-label', T.take_all);
            whole.forEach(function (t, k) {
                var b = document.createElement('button'); b.type = 'button'; b.className = 'vs-chip';
                var nm = names[t.gid + ':' + t.n];
                b.textContent = nm ? nm.label : fmt(T.take_fmt, { n: String.fromCharCode(65 + k) });
                var covered = S.scenes.map(function (_, i) { return takesFor(i).some(function (x) { return x.gid === t.gid && x.n === t.n; }); });
                var allOn = covered.some(Boolean) && covered.every(function (c, i) { return !c || (S.pick[i] && S.pick[i].gid === t.gid && S.pick[i].n === t.n); });
                b.classList.toggle('on', allOn);
                b.setAttribute('role', 'radio'); b.setAttribute('aria-checked', allOn ? 'true' : 'false');
                b.addEventListener('click', function () {
                    covered.forEach(function (c, i) { if (c) { S.pick[i] = { gid: t.gid, n: t.n }; S.hand[i] = false; } });
                    rebuildTrack(); renderScenes(); renderTimeline(); renderResult();
                    ae('take_all', { k: k, e: nm ? nm.engine : '' });
                });
                el.takes.appendChild(b);
            });
        }
        el.gOrigRow.hidden = !S.hasAudio;
        // Files of the takes (server copies — WAV master and MP3).
        el.files.innerHTML = '';
        Object.keys(S.gens).map(Number).sort(function (a, b) { return b - a; }).slice(0, 4).forEach(function (gid) {
            (S.gens[gid].variants || []).forEach(function (v, k) {
                (v.files || []).forEach(function (f) {
                    var a = document.createElement('a'); a.href = f.url; a.setAttribute('download', '');
                    a.textContent = (v.kind === 'music' ? '🎼' : '🔊 ' + takeName(gid, v.n)) + ' · ' + String(f.ext).toUpperCase();
                    el.files.appendChild(a);
                });
            });
        });
        el.retention.textContent = fmt(T.retention_note, { n: (S.boot && S.boot.file_ttl_days) || 30 });
    }
    function gainInput(input, out, key) {
        input.addEventListener('input', function () {
            S.gains[key] = input.value / 100; out.textContent = input.value + '%';
            player.setGains(S.gains);
        });
        input.addEventListener('change', function () { ae('gain', { k: key, v: +input.value }); });
    }
    gainInput(el.gAi, el.gAiV, 'ai'); gainInput(el.gMusic, el.gMusicV, 'music'); gainInput(el.gOrig, el.gOrigV, 'orig');
    player.setGains(S.gains);

    el.exp.addEventListener('click', function () { exportMp4(); });
    el.expWav.addEventListener('click', function () { exportWav(); });

    function expMsg(t, kind) { el.expMsg.hidden = !t; el.expMsg.textContent = t || ''; el.expMsg.className = 'tc-msg' + (kind ? ' ' + kind : ''); }

    async function mixForExport(withOrig) {
        var orig = null;
        if (withOrig && S.hasAudio && window.AudioDecoderRobust) {
            expMsg(T.export_decode);
            try { var r = await window.AudioDecoderRobust.robust(S.file, window.VideoSoundMixer.ctx(), { slug: SLUG, kind: 'video' }); orig = r && r.buffer; } catch (_) { orig = null; }
        }
        // The decode starts at the sound track's first sample, which the movie
        // may play later than the picture (MP4TrackRemux.soundStart): the mix is
        // on the movie's clock, so the original goes in that late.
        var origAt = 0;
        if (orig && window.VideoAudioLane && window.VideoAudioLane.soundStart) origAt = await window.VideoAudioLane.soundStart(S.file);
        return window.VideoSoundMixer.render({
            duration: S.meta.duration || (S.span.end), spanStart: S.span.start, spanEnd: S.span.end,
            ai: S.aiTrack, music: S.musicTrack, orig: orig, origStartSec: origAt,
            gains: S.gains, fadeIn: el.fadeIn.checked, fadeOut: el.fadeOut.checked
        });
    }
    function baseName() { return (S.file && S.file.name ? S.file.name.replace(/\.[^.]+$/, '') : 'video').slice(0, 80); }

    async function exportMp4() {
        if (S.exporting || !S.aiTrack) return;
        if ((S.meta.duration || 0) > 15 * 60) { expMsg(fmt(T.export_long, { min: 15 }), 'warn'); return; }
        S.exporting = true; el.exp.disabled = true;
        var t0 = performance.now();
        try {
            var mix = await mixForExport(true);
            expMsg(fmt(T.export_working, { n: 0 }));
            var name = S.file.name || 'video.mp4', blob = null, lane = '';
            var isMp4 = /\.(mp4|m4v|mov|3gp)$/i.test(name) || /mp4|quicktime/i.test(S.file.type || '');
            if (isMp4 && window.VideoAudioLane) {
                try {
                    var res = await window.VideoAudioLane.run({ file: S.file, buffer: mix, startSec: 0, bitrateKbps: 192, slug: SLUG,
                        onProgress: function (p) { expMsg(fmt(T.export_working, { n: Math.round(p * 100) })); } });
                    blob = res && res.blob; lane = 'native';
                } catch (e) { blob = null; lane = 'native_failed:' + ((e && (e.laneReason || e.message)) || '').slice(0, 60); }
            }
            if (!blob) {
                var F = window.FFmpegProcessor;
                if (!F) throw new Error('no-ffmpeg');
                if (typeof window.__ensureFFmpeg === 'function') await window.__ensureFFmpeg();
                if (!F.isReady || !F.isReady()) await F.init();
                var wavFile = new File([window.VideoSoundMixer.wav(mix)], 'mix.wav', { type: 'audio/wav' });
                var webm = /\.webm$/i.test(name);
                var outName = webm ? 'out.webm' : (isMp4 ? 'out.mp4' : 'out.mkv');
                try { F.onProgress && F.onProgress(function (r) { expMsg(fmt(T.export_working, { n: Math.round(r * 100) })); }); } catch (_) {}
                var r2 = await F.processMultiStreaming([S.file, wavFile], ['-map', '0:v:0', '-map', '1:a:0', '-c:v', 'copy', '-c:a', webm ? 'libopus' : 'aac', '-b:a', '192k', '-shortest'], outName);
                blob = r2 && r2.blob;
                lane = (lane ? lane + '>' : '') + 'ffmpeg';
                var ext = outName.split('.').pop();
                name = baseName() + '_sound.' + ext;
            } else {
                name = baseName() + '_sound.mp4';
            }
            if (!blob || blob.size < 1000) throw new Error('empty');
            if (window.__downloadFile) window.__downloadFile(blob, name);
            expMsg(T.export_done, 'good');
            try { if (window.__aeToolComplete) window.__aeToolComplete({ kind: 'mp4', lane: lane, ms: Math.round(performance.now() - t0), out: blob.size }); } catch (_) {}
            ae('export', { kind: 'mp4', lane: lane, ms: Math.round(performance.now() - t0), mb: Math.round(blob.size / 1048576) });
        } catch (e) {
            expMsg(T.export_failed, 'bad');
            ae('export_failed', { m: String((e && e.message) || e).slice(0, 100) });
        } finally {
            S.exporting = false; el.exp.disabled = false;
        }
    }
    async function exportWav() {
        if (!S.aiTrack || S.exporting) return;
        S.exporting = true; el.expWav.disabled = true;
        try {
            // The sound alone: AI track + music at their levels, only the voiced part.
            var mix = await window.VideoSoundMixer.render({
                duration: S.span.end - S.span.start, spanStart: 0, spanEnd: S.span.end - S.span.start,
                ai: S.aiTrack, music: S.musicTrack, orig: null, gains: S.gains, fadeIn: el.fadeIn.checked, fadeOut: el.fadeOut.checked
            });
            var blob = window.VideoSoundMixer.wav(mix);
            if (window.__downloadFile) window.__downloadFile(blob, baseName() + '_sound.wav');
            try { if (window.__aeToolComplete) window.__aeToolComplete({ kind: 'wav', out: blob.size }); } catch (_) {}
            ae('export', { kind: 'wav' });
        } catch (e) {
            toast(T.err_generic, 'error');
        } finally { S.exporting = false; el.expWav.disabled = false; }
    }

    // ── playback ──────────────────────────────────────────────────────────────
    function listenAvail() {
        [].forEach.call(el.listen.querySelectorAll('[data-listen]'), function (b) {
            var k = b.getAttribute('data-listen');
            if (k === 'orig') b.hidden = !S.hasAudio;
            if (k === 'ai') b.disabled = !S.aiTrack;
        });
        player.setGains(S.gains);
    }
    function setListen(m, quiet) {
        S.listen = m;
        [].forEach.call(el.listen.querySelectorAll('[data-listen]'), function (b) {
            var on = b.getAttribute('data-listen') === m;
            b.classList.toggle('on', on); b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        player.setMode(m);
        if (!quiet) ae('listen', { m: m });
    }
    el.listen.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('[data-listen]') : null;
        if (!b || b.disabled) return;
        setListen(b.getAttribute('data-listen'));
    });
    function togglePlay() {
        if (!S.previewOk) return;
        try { window.VideoSoundMixer.ctx().resume(); } catch (_) {}
        if (el.video.paused) el.video.play().catch(function () {}); else el.video.pause();
    }
    el.play.addEventListener('click', togglePlay);
    el.bigPlay.addEventListener('click', togglePlay);
    el.video.addEventListener('click', togglePlay);
    el.video.addEventListener('play', function () { el.play.textContent = '⏸'; el.play.setAttribute('aria-label', T.pause); el.bigPlay.hidden = true; });
    el.video.addEventListener('pause', function () { el.play.textContent = '▶'; el.play.setAttribute('aria-label', T.play); el.bigPlay.hidden = !S.previewOk; });
    el.video.addEventListener('timeupdate', function () {
        renderTime(); if (S.span) tl.playhead(el.video.currentTime - S.span.start);
        if (S.stopAt != null && el.video.currentTime >= S.stopAt) { S.stopAt = null; el.video.pause(); }
        if (S.span && !S.busy && sceneAt(el.video.currentTime) !== S.cmpRailAt) { S.cmpRailAt = sceneAt(el.video.currentTime); renderCmpRail(); }
    });
    el.video.addEventListener('play', function () { if (S.loop) { loopTick(); renderScenes(); } });
    el.video.addEventListener('pause', function () { if (S.loop) renderScenes(); });
    function renderTime() { el.time.textContent = clock(el.video.currentTime || 0) + ' / ' + clock(S.meta ? S.meta.duration : 0); }
    function seekTo(t) { try { el.video.currentTime = Math.max(0, Math.min((S.meta && S.meta.duration) || t, t)); } catch (_) {} if (S.span) tl.playhead(t - S.span.start); renderTime(); }
    function playScene(i) {
        var s = S.scenes[i]; if (!s || !S.span || !S.previewOk) return;
        S.loop = null;
        seekTo(S.span.start + s.start + 0.01);
        S.stopAt = S.span.start + s.end;
        try { window.VideoSoundMixer.ctx().resume(); } catch (_) {}
        el.video.play().catch(function () {});
        ae('play_scene', { i: i });
    }
    document.addEventListener('keydown', function (e) {
        if (el.work.hidden || e.ctrlKey || e.metaKey || e.altKey || !/^[1-9]$/.test(e.key)) return;
        if (window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : /^(INPUT|TEXTAREA|SELECT)$/.test((e.target && e.target.tagName) || '')) return;
        var i = S.loop ? S.loop.i : sceneAt(el.video.currentTime);
        var takes = i >= 0 ? takesFor(i) : [], k = +e.key - 1;
        if (takes.length < 2 || !takes[k]) return;
        e.preventDefault();
        pickTake(i, takes[k], k, 'key');
    });
    document.addEventListener('keydown', function (e) {
        if (e.code !== 'Space' || el.work.hidden) return;
        var t = e.target;
        if (t && (t.tagName === 'TEXTAREA' || t.tagName === 'INPUT' || t.tagName === 'BUTTON' || t.isContentEditable)) return;
        if (!el.root.contains(t) && t !== document.body) return;
        e.preventDefault(); togglePlay();
    });

    // ── history ───────────────────────────────────────────────────────────────
    function loadHistory() {
        if (!S.authed) return;
        api(q(cfg.api.history)).then(function (r) {
            if (r.status !== 200) return;
            var items = r.json.items || [];
            el.hist.hidden = !items.length;
            el.histList.innerHTML = '';
            S.histLive = {};
            items.forEach(function (it) {
                var li = document.createElement('li'); li.className = 'vs-hi';
                var sub = (it.created_at ? new Date(it.created_at).toLocaleDateString(cfg.locale) : '') + ' · ' + (T['engine_' + it.engine] || it.engine);
                var running = it.status !== 'done' && it.status !== 'failed';
                if (it.kind === 'compare' && T.history_compare) sub += ' · ' + T.history_compare;
                if (it.status === 'failed') sub += ' · ' + T.status_failed;
                else if (running) sub += ' · ' + T.status_running;
                else if (it.expires_at) sub += ' · ' + fmt(T.expires_on, { date: new Date(it.expires_at).toLocaleDateString(cfg.locale) });
                li.innerHTML = '<div class="vs-hi-main"><div class="vs-hi-title"></div><div class="vs-hi-sub"></div></div><div class="vs-hi-act"></div>';
                li.querySelector('.vs-hi-title').textContent = it.prompt || T.untitled;
                li.querySelector('.vs-hi-sub').textContent = sub;
                var act = li.querySelector('.vs-hi-act');
                if (running) {
                    // «in progress» used to be all this row said — and clicking it
                    // did nothing (rage_click, 2026-09-29). Now: parts, time since
                    // the order, time left; and a way out.
                    var live = document.createElement('span'); live.className = 'vs-hi-live';
                    li.querySelector('.vs-hi-sub').appendChild(live);
                    S.histLive[it.id] = live;
                    if (P) live.textContent = ' · ' + P.line(it.progress, T, null);
                    var cb = document.createElement('button'); cb.type = 'button'; cb.className = 'tc-btn ghost sm'; cb.textContent = T.cancel;
                    cb.addEventListener('click', function () { cancelGeneration(it.id); });
                    act.appendChild(cb);
                }
                if (it.status === 'done' && (it.variants || []).length) {
                    var b = document.createElement('button'); b.type = 'button'; b.className = 'tc-btn ghost sm'; b.textContent = S.span ? T.history_apply : T.history_open;
                    b.addEventListener('click', function () { openGeneration(it.id, false); });
                    act.appendChild(b);
                    var f = (it.variants[0].files || []).filter(function (x) { return x.master; })[0];
                    if (f) { var a = document.createElement('a'); a.className = 'tc-btn ghost sm'; a.href = f.url; a.setAttribute('download', ''); a.textContent = '⬇ ' + String(f.ext).toUpperCase(); act.appendChild(a); }
                }
                el.histList.appendChild(li);
            });
            // A reload lost the page's memory of what it ordered; the server did
            // not. Follow the newest generation still on the server.
            var run = P ? P.running(items) : null;
            if (run && !S.gid) attach(run);
            else if (S.gid && S.lastStatus) renderLive(S.lastStatus, S.etaKey && window.__acETA ? window.__acETA.remainingSec(S.etaKey) : null);
        });
    }
    async function openGeneration(id, fromLink) {
        var r = await api(q(cfg.api.status + '/' + id));
        if (r.status !== 200) return;
        var d = r.json;
        if (d.status === 'running' || d.status === 'queued') { attach(d); return; }
        if (d.status !== 'done') return;
        if (!S.span) { toast(T.history_need_video, 'info'); S.pendingGen = d; return; }
        await absorb(d, true);
        rebuildTrack(); renderScenes(); renderTimeline(); renderResult(); setListen('ai');
        toast(T.history_applied, 'success');
        ae('history_apply', { from: fromLink ? 'link' : 'list' });
    }

    // ── examples: было — стало, and every engine on the same clip ────────────
    /* Each card plays its clip with its own soundtrack (the default engine's
       take, muxed in) until the person picks another engine: then the four
       takes of that clip (one run per engine, loudness matched within the clip
       — config/video-sound.php `tracks`) are decoded once and played through
       the page's synced player (mixer.js), so a chip swaps the sound in place
       while the picture runs on. They are fetched when the clip first plays,
       so the first switch rarely waits. */
    function exampleEngines() {
        [].forEach.call(document.querySelectorAll('.vs-exc-price[data-ex-prices]'), function (em) { em.__render && em.__render(); });
    }
    [].forEach.call(document.querySelectorAll('.vs-exc'), function (card) {
        var v = card.querySelector('video'), btn = card.querySelector('.vs-exc-play'), badge = card.querySelector('.vs-exc-sound');
        var ab = card.querySelectorAll('[data-ab]'), chips = card.querySelectorAll('[data-ex-track]');
        var priceEl = card.querySelector('.vs-exc-price');
        var key = card.getAttribute('data-ex'), dflt = card.getAttribute('data-ex-default') || '';
        var mode = 'after', track = dflt, player = null, bufs = null, loading = null;
        var prices = {}; try { prices = JSON.parse((priceEl && priceEl.getAttribute('data-ex-prices')) || '{}'); } catch (_) {}
        var seconds = priceEl ? +priceEl.getAttribute('data-ex-seconds') || 8 : 8;

        function engOf(t) { return String(t).split('-')[0]; }
        function renderPrice() {
            if (!priceEl) return;
            var e = engOf(track), n = prices[e];
            if (!n) return;
            var num = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
            var txt = fmt(T.ex_price, { s: seconds, price: fmt(T.price_fmt, { n: num }) });
            if (e === 'mirelo') txt += ' · ' + fmt(T.ex_takes, { v: 2 });
            var m = money(n);
            priceEl.textContent = txt + (m ? ' ' + m : '');
            priceEl.setAttribute('data-ex-tokens', String(n));
        }
        if (priceEl) priceEl.__render = renderPrice;
        function renderChips() {
            [].forEach.call(chips, function (c) {
                var on = mode === 'after' && c.getAttribute('data-ex-track') === track;
                c.classList.toggle('on', on); c.setAttribute('aria-checked', on ? 'true' : 'false');
            });
        }
        function setAb(m) {
            mode = m;
            [].forEach.call(ab, function (b) { var on = b.getAttribute('data-ab') === m; b.classList.toggle('on', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); });
            if (player) player.setMode(m === 'before' ? 'none' : 'ai');
            else v.muted = m === 'before' || v.paused;
            badge.textContent = m === 'before' ? '🔇' : '🔊';
            renderChips();
        }
        function load() {
            if (loading) return loading;
            var M = window.VideoSoundMixer;
            if (!M) return Promise.reject(new Error('no-mixer'));
            var list = [].map.call(chips, function (c) { return [c.getAttribute('data-ex-track'), c.getAttribute('data-src')]; });
            loading = Promise.all(list.map(function (x) { return M.decode(x[1]).then(function (b) { return [x[0], b]; }); }))
                .then(function (pairs) { bufs = {}; pairs.forEach(function (x) { bufs[x[0]] = x[1]; }); return bufs; })
                .catch(function (e) { loading = null; throw e; });
            return loading;
        }
        function usePlayer() {
            if (player || !bufs) return;
            player = window.VideoSoundMixer.player(v, { hasOrig: false });
            player.setGains({ ai: 1, music: 0, orig: 0 });
            player.setMode(mode === 'before' ? 'none' : 'ai');
            player.setBuffers({ ai: bufs[track] }, 0, Math.max(v.duration || 0, seconds));
        }
        function start() {
            // Only one example plays at a time — two soundtracks over each other help nobody.
            [].forEach.call(document.querySelectorAll('.vs-exc video'), function (o) { if (o !== v && !o.paused) o.pause(); });
            if (player) { try { window.VideoSoundMixer.ctx().resume(); } catch (_) {} }
            else v.muted = mode === 'before';
            v.play().catch(function () {});
            // Ready before the first switch: four ~128 KB takes, not on a metered link.
            var c = navigator.connection;
            if (!(c && c.saveData)) load().catch(function () {});
        }
        function pick(t) {
            var t0 = performance.now(), was = track;
            track = t;
            setAb('after');
            renderPrice();
            if (player) { player.setBuffers({ ai: bufs[t] }, 0, Math.max(v.duration || 0, seconds)); if (v.paused) start(); }
            else if (t === dflt) { if (v.paused) start(); else v.muted = false; }
            else {
                [].forEach.call(chips, function (c) { if (c.getAttribute('data-ex-track') === t) c.setAttribute('aria-busy', 'true'); });
                if (v.paused) start();
                load().then(function () {
                    if (track !== t) return;
                    usePlayer();
                    if (v.paused) start();
                }).catch(function () {
                    // The takes did not come: stay with the clip's own sound and say nothing louder than the chip.
                    track = dflt; renderChips(); renderPrice(); if (!v.paused) v.muted = false;
                }).then(function () { [].forEach.call(chips, function (c) { c.removeAttribute('aria-busy'); }); });
            }
            ae('example_engine', { k: key, t: t, from: was, ready_ms: Math.round(performance.now() - t0), playing: v.paused ? 0 : 1 });
        }
        btn.addEventListener('click', function () { start(); ae('example', { k: key, m: mode, t: track }); });
        v.addEventListener('click', function () { if (v.paused) start(); else v.pause(); });
        v.addEventListener('play', function () { btn.hidden = true; });
        v.addEventListener('pause', function () { btn.hidden = false; });
        [].forEach.call(ab, function (b) { b.addEventListener('click', function () { setAb(b.getAttribute('data-ab')); if (v.paused) start(); ae('example_ab', { k: key, m: mode, t: track }); }); });
        [].forEach.call(chips, function (c) { c.addEventListener('click', function () { pick(c.getAttribute('data-ex-track')); }); });
        // Hovering or focusing the engines is the moment before a switch: fetch then.
        var pre = function () { var c = navigator.connection; if (!(c && c.saveData)) load().catch(function () {}); };
        [].forEach.call(chips, function (c) { c.addEventListener('pointerenter', pre, { once: true }); c.addEventListener('focus', pre, { once: true }); });
        setAb('after');
    });
})();
